import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import useRefreshToken from "../../hooks/useRefreshToken";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import SplashScreen from "./SplashScreen";
import { clearGuestMessageCount } from "../../config/guestLimits";

export default function PersistLogin() {
  const { accessToken, user, setLoading, refreshProfile } = useAuth();
  const refresh = useRefreshToken();
  // Decide SYNCHRONOUSLY whether this visitor needs to wait for a session
  // refresh. Deferring this to the effect made the very first render show
  // the splash for one frame — which wiped the prerendered page, painted the
  // footer near the top of the viewport (0.86 CLS) and reset LCP.
  const [isInitialLoad, setIsInitialLoad] = useState(() => {
    if (typeof navigator !== 'undefined' && navigator.userAgent.includes('ReactSnap')) {
      return false;
    }
    try {
      const isLoggedOut = localStorage.getItem("isLoggedOut") === "true";
      const hasSession = localStorage.getItem("hasSession") === "true";
      // Only a returning logged-in user waits for the refresh gate.
      return hasSession && !isLoggedOut;
    } catch {
      return false;
    }
  });
  const axiosPrivate = useAxiosPrivate();

  // Skip all API calls during react-snap pre-rendering
  const isPrerendering = typeof navigator !== 'undefined' && navigator.userAgent.includes('ReactSnap');

  useEffect(() => {
    if (isPrerendering) {
      setIsInitialLoad(false);
      setLoading(false);
      return;
    }

    let isMounted = true;

    const verifyRefreshToken = async () => {
      const isLoggedOut = localStorage.getItem("isLoggedOut") === "true";
      // Positive session marker set on every successful refresh/login. The
      // refresh token is an httpOnly cookie the client can't see, so without
      // this flag a first-time visitor pays a guaranteed-401 round trip
      // before anything renders.
      const hasSession = localStorage.getItem("hasSession") === "true";

      if (isLoggedOut) {
        localStorage.removeItem("guestMergeAttempted");
        localStorage.removeItem("guestMergeAttempts");
        localStorage.removeItem("guestChatMessages");
        clearGuestMessageCount();
        // A stale id now points at a conversation that HAS an owner, so the
        // next guest's turns would append into the previous user's chat.
        localStorage.removeItem("guestSessionId");
      }

      if (!hasSession) {
        // No positive session marker means anonymous. Do not probe the refresh
        // endpoint: that guarantees a 401, logs two errors and spends a network
        // round trip on every first visit. Successful login/refresh stamps the
        // marker, so returning signed-in users still take the refresh path below.
        if (isMounted) {
          setIsInitialLoad(false);
        }
        return;
      }

      try {
        if (!accessToken && !isLoggedOut) {
          await refresh();
        }
      } catch (err) {
        console.error("PersistLogin: Could not refresh token.", err);
      } finally {
        if (isMounted) {
          setIsInitialLoad(false);
        }
      }
    };

    verifyRefreshToken();

      return () => {
        isMounted = false;
      };
    }, [accessToken, refresh, isPrerendering, setLoading]);

    useEffect(() => {
    let isMounted = true;

    const fetchUser = async () => {
    if (accessToken && !user) {
      try {
      await refreshProfile(accessToken); // This sets the user

        // *** NEW: Merge guest chat logic ***
        // This runs after a token is present but before the user object is
        // We should run it AFTER refreshProfile sets the user.
        // Let's modify the logic to depend on the *new* user object.
        // Wait, no, refreshProfile *sets* the user, so 'user' is stale.
        // Let's check 'accessToken' and the *lack* of the 'user' object.
        // And check our one-time flag.

        // Retry failed merges on later mounts (up to a cap) — the old logic
        // set a one-shot flag in `finally`, so a single network blip lost the
        // guest's estimate forever.
        const MAX_GUEST_MERGE_ATTEMPTS = 3;
        const mergeDone = localStorage.getItem("guestMergeAttempted") === "true";
        const mergeAttempts = parseInt(localStorage.getItem("guestMergeAttempts") || "0", 10);
        const guestChatExists = localStorage.getItem("guestChatMessages");

        if (accessToken && !mergeDone && mergeAttempts < MAX_GUEST_MERGE_ATTEMPTS && guestChatExists) {
        try {
          // The backend re-parents by guest_session_id and falls back to
          // rebuilding from history. Posting no body meant merged_count was
          // always 0 while the client wiped the transcript anyway.
          let guestHistory = [];
          try {
            guestHistory = JSON.parse(guestChatExists)
              .filter((m) => m && m.id !== "guest_welcome" && m.text)
              .map((m) => ({
                role: m.sender === "bot" ? "assistant" : "user",
                content: m.text,
              }));
          } catch {
            guestHistory = [];
          }
          // We use axiosPrivate, which is now configured with interceptors
          await axiosPrivate.post("/chat/merge-guest/", {
            guest_conversations: [
              {
                history: guestHistory,
                guest_session_id: localStorage.getItem("guestSessionId") || null,
              },
            ],
          });
          // Clear guest chat from storage ONLY on success
          localStorage.removeItem("guestChatMessages");
          clearGuestMessageCount();
          localStorage.removeItem("guestSessionId");
          localStorage.removeItem("guestMergeAttempts");
          localStorage.setItem("guestMergeAttempted", "true");
        } catch (mergeErr) {
          console.error("PersistLogin: Failed to merge guest chat.", mergeErr);
          // Leave the done-flag unset so the next mount retries.
          localStorage.setItem("guestMergeAttempts", String(mergeAttempts + 1));
        }
        }
        // *** END NEW MERGE LOGIC ***

      } catch (err) {
      console.error("PersistLogin: Could not fetch user profile.", err);
      }
    }
    if (isMounted) {
      setLoading(false);
    }
    };

    if (!isInitialLoad) {
    fetchUser();
    }

    return () => {
    isMounted = false;
    };
  }, [accessToken, user, isInitialLoad, refreshProfile, setLoading, axiosPrivate]);

  return isInitialLoad ? <SplashScreen loadingMessage="Getting AI warmed up..." /> : <Outlet />;
}
